/**
 * TanStack Query hooks — Registre assureurs (9.V.1 backend, UI 9.V.2b).
 *
 * Routes API : GET /v1/insurers · GET /v1/insurers/:id
 * (CRUD structurel assureur = 9.V.1, non exposé dans cette UI carnet 9.V.2b).
 */

import type { DbPage } from "@kollectivia/shared";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../api";

export type InsurerStatus = "active" | "inactive";

export type InsurerRow = {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  short_code: string | null;
  status: InsurerStatus;
  provenance: "seed" | "manual" | "migration_backfill";
  notes: string | null;
  created_at: string;
  updated_at: string;
  /**
   * 9.BC.1 — l'adresse à laquelle une demande partirait, résolue par le serveur.
   *
   * `null` = cet assureur n'est joignable par aucune adresse, et l'envoi le
   * refusera. Le savoir AVANT de le sélectionner évite de découvrir le problème
   * au moment d'envoyer. Le web ne calcule rien : il affiche ce que le serveur
   * a résolu avec la règle qui composera réellement le destinataire.
   */
  submission_email?: string | null;
};

export type InsurerListParams = {
  cursor?: string | null;
  limit?: number;
  q?: string;
  status?: InsurerStatus | "all";
};

function buildInsurersPath(params: InsurerListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.q) sp.set("q", params.q);
  if (params.status) sp.set("status", params.status);
  const qs = sp.toString();
  return qs ? `/v1/insurers?${qs}` : "/v1/insurers";
}

export function useInsurers(params: InsurerListParams = {}) {
  return useQuery({
    queryKey: ["insurers", "list", params],
    queryFn: () => api.get<DbPage<InsurerRow>>(buildInsurersPath(params)),
    placeholderData: (prev) => prev,
  });
}

export function useInsurer(id: string | undefined) {
  return useQuery({
    queryKey: ["insurers", "detail", id],
    queryFn: () => api.get<InsurerRow>(`/v1/insurers/${id}`),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// useInsurerCatalog — source dynamique unique pour tous les pickers web
// (9.V.2c-2b). Remplace la constante statique `INSURERS` de @kollectivia/shared
// comme source des choix. La validation métier (existence / statut / tenant /
// autorisation) reste au backend tenant-aware (9.V.2c-1/2a) ; ce hook ne fait
// que projeter les choix affichables + résoudre des libellés lisibles.
// ---------------------------------------------------------------------------

/** Option affichable dans un picker (code + libellé humain du registre). */
export type InsurerOption = {
  value: string;
  label: string;
  /**
   * 9.BE.1 — l'adresse à laquelle une demande partirait, résolue par le serveur.
   *
   * `null` = injoignable, et l'envoi le refusera. Le savoir AVANT de cocher
   * évite de le découvrir au moment d'envoyer. Le web ne calcule rien : la
   * valeur vient de `app.insurer_submission_email` (9.BC.1), la même fonction
   * qui compose réellement le destinataire.
   */
  submissionEmail: string | null;
};

export type InsurerCatalog = {
  /** Assureurs ACTIFS uniquement — seuls choix valides pour une NOUVELLE opération. */
  activeOptions: InsurerOption[];
  /**
   * Libellé lisible pour un code donné :
   *   - actif                → nom du registre
   *   - inactif (nom connu)  → « Nom (inactif) »
   *   - inconnu du registre  → « code (inactif) » (fallback lisible basé sur le code)
   *   - registre non résolu (chargement/erreur) → code brut (aucune assertion inactif)
   * Ne renvoie JAMAIS une clé de traduction brute.
   */
  labelFor: (code: string) => string;
  /** true si le code correspond à un assureur ACTIF du tenant. */
  isActive: (code: string) => boolean;
  isLoading: boolean;
  isError: boolean;
  /** true seulement une fois chargé sans erreur ET aucun assureur actif. */
  isEmpty: boolean;
  refetch: () => void;
};

/**
 * Un seul fetch `status:"all"` (limit 200) :
 *   - `activeOptions` filtre `status==='active'` → choix des nouvelles opérations
 *   - la liste complète (actifs + inactifs) alimente `labelFor` pour rester
 *     lisible sur les données historiques dont le code est désactivé.
 *
 * Edge documenté (9.V.2c-2b) : au-delà de 200 assureurs d'un même tenant, la
 * page est tronquée (pas de pagination multi-page dans ce chunk — hors scope).
 * Volume réel attendu : ~8 seed + ajouts manuels ponctuels, très loin de 200.
 */
export function useInsurerCatalog(): InsurerCatalog {
  const { t } = useTranslation();
  const query = useInsurers({ status: "all", limit: 200 });

  const rows = useMemo<InsurerRow[]>(() => query.data?.items ?? [], [query.data]);
  const dataReady = !query.isLoading && !query.isError;

  const nameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) m.set(r.code, r.name);
    return m;
  }, [rows]);

  const activeSet = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) if (r.status === "active") s.add(r.code);
    return s;
  }, [rows]);

  const activeOptions = useMemo<InsurerOption[]>(
    () =>
      rows
        .filter((r) => r.status === "active")
        .map((r) => ({
          value: r.code,
          label: r.name,
          submissionEmail: r.submission_email ?? null,
        })),
    [rows],
  );

  return useMemo<InsurerCatalog>(
    () => ({
      activeOptions,
      isActive: (code: string) => activeSet.has(code),
      labelFor: (code: string) => {
        // Registre non résolu : ne rien affirmer sur le statut, code brut lisible.
        if (!dataReady) return code;
        const name = nameByCode.get(code) ?? code;
        if (activeSet.has(code)) return name;
        return t("common.insurers.inactive_suffix", { name });
      },
      isLoading: query.isLoading,
      isError: query.isError,
      isEmpty: dataReady && activeOptions.length === 0,
      refetch: () => {
        void query.refetch();
      },
    }),
    [activeOptions, activeSet, nameByCode, dataReady, query, t],
  );
}
